import { fetch } from '@atlassian/eoc-fetch';
import type {
	AddRotationParameters,
	DeleteRotationParameters,
	EditRotationParameters,
	GetRotationParameters,
	GetRotationResponse,
	OrderRotationParameters,
} from '@atlassian/eoc-schedule-common/types/edit';
import type { GetTimeTemplatesResponse as ScheduleTimeTemplatesResponse } from '@atlassian/eoc-schedule-common/types/time';

import { urls } from '../../../common/constants/urls';

const orderRotation = ({ scheduleId, after, rotationId }: OrderRotationParameters): Promise<{}> => {
	return fetch(
		urls.schedules.scheduleId.rotations.rotationId.order.url({
			rotationId,
			scheduleId,
		}),
		{
			method: 'PATCH',
			body: {
				after,
			},
		},
	);
};

const getRotation = ({
	scheduleId,
	rotationId,
}: GetRotationParameters): Promise<GetRotationResponse> => {
	return fetch<GetRotationResponse>(
		urls.schedules.scheduleId.rotations.rotationId.url({
			scheduleId,
			rotationId,
		}),
		{
			method: 'GET',
		},
	);
};

const deleteRotation = ({ scheduleId, rotationId }: DeleteRotationParameters): Promise<{}> => {
	return fetch(
		urls.schedules.scheduleId.rotations.rotationId.url({
			scheduleId,
			rotationId,
		}),
		{
			method: 'DELETE',
		},
	);
};

const addRotation = ({
	name,
	responders,
	restrictions,
	rotationType,
	hasEndTime,
	rotationPeriod,
	scheduleId,
	timeTemplateId,
	timingType,
	startTimeRaw,
	endTimeRaw,
}: AddRotationParameters): Promise<{}> => {
	return fetch(
		urls.schedules.scheduleId.rotations.url({
			scheduleId,
		}),
		{
			method: 'POST',
			body: {
				name,
				responders,
				restrictions,
				rotationType,
				timeTemplateId,
				timingType,
				rotationPeriod,
				startTimeRaw,
				endTimeRaw,
				hasEndTime,
			},
		},
	);
};

const editRotation = ({
	id,
	name,
	responders,
	restrictions,
	rotationType,
	rotationPeriod,
	hasEndTime,
	timingType,
	scheduleId,
	timeTemplateId,
	startTimeRaw,
	endTimeRaw,
}: EditRotationParameters): Promise<{}> => {
	return fetch(
		urls.schedules.scheduleId.rotations.rotationId.url({
			scheduleId,
			rotationId: id,
		}),
		{
			method: 'PUT',
			body: {
				id,
				name,
				responders,
				restrictions,
				rotationType,
				rotationPeriod,
				timeTemplateId,
				timingType,
				startTimeRaw,
				endTimeRaw,
				hasEndTime,
			},
		},
	);
};

const getScheduleTimeTemplates = (): Promise<ScheduleTimeTemplatesResponse> => {
	return fetch<ScheduleTimeTemplatesResponse>(urls.schedules.timeTemplates.rotation.url(), {
		method: 'GET',
	});
};

export const endpoints: {
	addRotation: ({
		name,
		responders,
		restrictions,
		rotationType,
		hasEndTime,
		rotationPeriod,
		scheduleId,
		timeTemplateId,
		timingType,
		startTimeRaw,
		endTimeRaw,
	}: AddRotationParameters) => Promise<{}>;
	deleteRotation: ({ scheduleId, rotationId }: DeleteRotationParameters) => Promise<{}>;
	editRotation: ({
		id,
		name,
		responders,
		restrictions,
		rotationType,
		rotationPeriod,
		hasEndTime,
		timingType,
		scheduleId,
		timeTemplateId,
		startTimeRaw,
		endTimeRaw,
	}: EditRotationParameters) => Promise<{}>;
	getRotation: ({ scheduleId, rotationId }: GetRotationParameters) => Promise<GetRotationResponse>;
	getScheduleTimeTemplates: () => Promise<ScheduleTimeTemplatesResponse>;
	orderRotation: ({ scheduleId, after, rotationId }: OrderRotationParameters) => Promise<{}>;
} = {
	deleteRotation,
	getRotation,
	orderRotation,
	addRotation,
	editRotation,
	getScheduleTimeTemplates,
};
